import {
  AutomationJsonSchema,
  AutomationSchemaRef,
  DatasetType,
  RuntimeOutputName,
} from '@/types/automationFields.ts';
import { TransformDataOutput, TransformDataStep } from '@/types/automationSteps.ts';
import { Group, Stack } from '@mantine/core';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StepFieldsAddButton from '@/pages/Editor/components/StepFieldsAddButton.tsx';
import StepFieldsRemoveButton from '@/pages/Editor/components/StepFieldsRemoveButton.tsx';
import { STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import { generateUid } from '@/utils/misc.ts';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { useMemo, useState } from 'react';
import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import {
  outputTypeSupportsSchemaRef,
  schemaSupportsOutputType,
} from '@/pages/Editor/components/StepSidebar/StepForms/TransformDataStep/transformDataOutputSchemaUtils.ts';
import { RuntimeOutputNameInput } from '@/pages/Editor/components/SharedFields/RuntimeOutputNameField.tsx';
import OutputPinButton from '@/pages/Editor/components/StepSidebar/OutputPinButton.tsx';

type Props = {
  step: TransformDataStep;
};

type Fields = {
  output_name: string;
  runtime_output_name?: RuntimeOutputName | null;
  output_type: DatasetType;
  output_schema_ref?: AutomationSchemaRef | null;
  data_outputs?: TransformDataOutput[];
};

const DATASET_OUTPUT_OPTIONS = [
  { label: 'File', value: DatasetType.File },
  { label: 'List', value: DatasetType.Dataframe },
  { label: 'Json', value: DatasetType.Json },
];

const MAX_TRANSFORM_DATA_OUTPUTS = 5;

const makeInitialDataOutput = (fields: Fields, step: TransformDataStep): TransformDataOutput => {
  const outputType = fields.output_type ?? step.output_type ?? DatasetType.Json;

  return {
    output_id: generateUid(),
    name: fields.output_name || step.output_name || step.name,
    output_type: outputType,
    schema_ref: outputTypeSupportsSchemaRef(outputType)
      ? (fields.output_schema_ref ?? step.output_schema_ref ?? null)
      : null,
    runtime_output_name: fields.runtime_output_name ?? step.runtime_output_name ?? null,
  };
};

const schemaRefToValue = (schemaRef?: AutomationSchemaRef | null) => {
  return schemaRef
    ? JSON.stringify({ schema_id: schemaRef.schema_id, version: schemaRef.version })
    : null;
};

const valueToSchemaRef = (value: string | null): AutomationSchemaRef | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as AutomationSchemaRef;
  } catch {
    return null;
  }
};

const schemaRefMatches = (schema: AutomationJsonSchema, schemaRef?: AutomationSchemaRef | null) =>
  schema.schema_id === schemaRef?.schema_id && schema.version === schemaRef?.version;

const makeSchemaLabel = (schemaRef: AutomationSchemaRef) =>
  `${schemaRef.schema_id} v${schemaRef.version}`;

const TransformDataOutputFields = ({ step }: Props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const [confirmSingleOutput, setConfirmSingleOutput] = useState(false);
  const dataOutputs = fields.data_outputs ?? [];
  const isMultiOutput = dataOutputs.length > 0;
  const schemas = latestVersion?.schemas ?? [];
  const invalidSingleOutputType = useStepSpecificError(step, StepErrorType.InvalidTransformOutputType);
  const invalidSingleSchemaRef = useStepSpecificError(step, StepErrorType.InvalidSchemaRef);
  const invalidSingleRuntimeOutputName = useStepSpecificError(
    step,
    StepErrorType.InvalidRuntimeOutputName,
  );

  const switchToSingleOutput = () => {
    const firstOutput = dataOutputs[0];
    onChangeNoDelay({
      data_outputs: [],
      output_name: firstOutput?.name ?? fields.output_name,
      runtime_output_name: firstOutput?.runtime_output_name ?? fields.runtime_output_name ?? null,
      output_type: firstOutput?.output_type ?? fields.output_type,
      output_schema_ref:
        firstOutput && outputTypeSupportsSchemaRef(firstOutput.output_type)
          ? (firstOutput.schema_ref ?? null)
          : null,
    });
  };

  const setMultiOutput = (checked: boolean) => {
    if (checked) {
      onChangeNoDelay({
        data_outputs: dataOutputs.length > 0 ? dataOutputs : [makeInitialDataOutput(fields, step)],
      });
      return;
    }

    if (dataOutputs.length > 1) {
      setConfirmSingleOutput(true);
      return;
    }

    switchToSingleOutput();
  };

  return (
    <>
      <Stack>
        {!isMultiOutput && (
          <Stack gap="xs">
            <StyledTextInput
              label="Output Name"
              value={fields.output_name}
              placeholder="Name of the output produced by this step"
              onChange={(event) => {
                const value = event.currentTarget.value.replace(/[/\n]/g, '');
                onChange({ output_name: value });
              }}
              required={false}
              maxLength={STEP_FIELD_MAX_LENGTH}
            />
            <StyledSelect
              label="Data Output Type"
              placeholder="Select Output Data Type"
              data={DATASET_OUTPUT_OPTIONS}
              value={fields.output_type}
              onChange={(value) => {
                if (!value) {
                  return;
                }
                const outputType = value as DatasetType;
                onChangeNoDelay({
                  output_type: outputType,
                  output_schema_ref: outputTypeSupportsSchemaRef(outputType)
                    ? (fields.output_schema_ref ?? null)
                    : null,
                });
              }}
              required={true}
              allowDeselect={false}
              clearable={false}
              error={invalidSingleOutputType}
            />
            <TransformOutputSchemaSelect
              outputType={fields.output_type}
              schemas={schemas}
              value={fields.output_schema_ref}
              error={invalidSingleSchemaRef}
              onChange={(schemaRef) => {
                onChangeNoDelay({ output_schema_ref: schemaRef });
              }}
            />
            <RuntimeOutputNameInput
              step={step}
              value={fields.runtime_output_name ?? null}
              onChange={(runtimeOutputName) => onChange({ runtime_output_name: runtimeOutputName })}
              onChangeNoDelay={(runtimeOutputName) =>
                onChangeNoDelay({ runtime_output_name: runtimeOutputName })
              }
              error={invalidSingleRuntimeOutputName}
            />
          </Stack>
        )}

        {isMultiOutput && (
          <Stack gap="xs">
            {dataOutputs.map((dataOutput, idx) => (
              <TransformDataOutputRow
                key={dataOutput.output_id}
                dataOutput={dataOutput}
                step={step}
                inputIndex={idx}
                onChangeNoDelay={(updatedOutput) => {
                  onChangeNoDelay({
                    data_outputs: dataOutputs.map((output) =>
                      output.output_id === updatedOutput.output_id ? updatedOutput : output,
                    ),
                  });
                }}
                onChangeDelay={(updatedOutput) => {
                  onChange({
                    data_outputs: dataOutputs.map((output) =>
                      output.output_id === updatedOutput.output_id ? updatedOutput : output,
                    ),
                  });
                }}
                schemas={schemas}
                onDelete={() => {
                  if (dataOutputs.length === 1) {
                    setMultiOutput(false);
                    return;
                  }
                  onChangeNoDelay({
                    data_outputs: dataOutputs.filter(
                      (output) => output.output_id !== dataOutput.output_id,
                    ),
                  });
                }}
              />
            ))}

            {dataOutputs.length < MAX_TRANSFORM_DATA_OUTPUTS && (
              <StepFieldsAddButton
                stepId={step.step_id}
                elementCount={dataOutputs.length}
                onClick={() => {
                  onChangeNoDelay({
                    data_outputs: [
                      ...dataOutputs,
                      {
                        output_id: generateUid(),
                        name: '',
                        output_type: DatasetType.Json,
                        schema_ref: null,
                        runtime_output_name: null,
                      },
                    ],
                  });
                }}
              />
            )}
          </Stack>
        )}

        <StyledSwitch
          label="Multiple outputs"
          labelPosition="left"
          checked={isMultiOutput}
          onChange={(event) => setMultiOutput(event.currentTarget.checked)}
          styles={{ body: { justifyContent: 'space-between' }, label: { marginBottom: 0 } }}
        />
      </Stack>

      <ConfirmationModal
        opened={confirmSingleOutput}
        onClose={() => setConfirmSingleOutput(false)}
        title="Switch to Single Output"
        message="Switching back to a single output will keep only the first output."
        onConfirm={() => {
          switchToSingleOutput();
          setConfirmSingleOutput(false);
        }}
        confirmText="Switch"
      />
    </>
  );
};

type RowProps = {
  dataOutput: TransformDataOutput;
  step: TransformDataStep;
  inputIndex: number;
  onChangeNoDelay: (updatedOutput: TransformDataOutput) => void;
  onChangeDelay: (updatedOutput: TransformDataOutput) => void;
  schemas: AutomationJsonSchema[];
  onDelete: () => void;
};

const TransformDataOutputRow = ({
  dataOutput,
  step,
  inputIndex,
  onChangeNoDelay,
  onChangeDelay,
  schemas,
  onDelete,
}: RowProps) => {
  const emptyOutputName = useStepSpecificError(step, StepErrorType.EmptyOutputName, inputIndex);
  const invalidOutput = useStepSpecificError(step, StepErrorType.InvalidTransformOutputType, inputIndex);
  const invalidSchemaRef = useStepSpecificError(step, StepErrorType.InvalidSchemaRef, inputIndex);
  const invalidRuntimeOutputName = useStepSpecificError(
    step,
    StepErrorType.InvalidRuntimeOutputName,
    inputIndex,
  );
  const isNewStep = useIsNewStep(step.step_id);

  return (
    <Stack
      style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }}
      bg="#F6F6F6"
      p="xs"
      gap="xs"
    >
      <Group gap="xs" wrap="nowrap" align="start">
        <StyledTextInput
          label="Name"
          value={dataOutput.name}
          placeholder="Output Name"
          onChange={(event) => {
            const value = event.currentTarget.value.replace(/[/\n]/g, '');
            onChangeDelay({ ...dataOutput, name: value });
          }}
          required={true}
          maxLength={STEP_FIELD_MAX_LENGTH}
          w="100%"
          error={
            emptyOutputName || (!dataOutput.name && !isNewStep ? 'Name cannot be empty' : undefined)
          }
        />
        <OutputPinButton step={step} outputId={dataOutput.output_id} />
        <StepFieldsRemoveButton onDelete={onDelete} />
      </Group>

      <StyledSelect
        label="Data Output Type"
        placeholder="Select Output Data Type"
        data={DATASET_OUTPUT_OPTIONS}
        value={dataOutput.output_type}
        onChange={(value) => {
          if (!value) {
            return;
          }
          const outputType = value as DatasetType;
          onChangeNoDelay({
            ...dataOutput,
            output_type: outputType,
            schema_ref: outputTypeSupportsSchemaRef(outputType)
              ? (dataOutput.schema_ref ?? null)
              : null,
          });
        }}
        required={true}
        allowDeselect={false}
        clearable={false}
        error={invalidOutput}
      />
      <TransformOutputSchemaSelect
        outputType={dataOutput.output_type}
        schemas={schemas}
        value={dataOutput.schema_ref}
        error={invalidSchemaRef}
        onChange={(schemaRef) => {
          onChangeNoDelay({ ...dataOutput, schema_ref: schemaRef });
        }}
      />
      <RuntimeOutputNameInput
        step={step}
        value={dataOutput.runtime_output_name ?? null}
        onChange={(runtimeOutputName) =>
          onChangeDelay({ ...dataOutput, runtime_output_name: runtimeOutputName })
        }
        onChangeNoDelay={(runtimeOutputName) =>
          onChangeNoDelay({ ...dataOutput, runtime_output_name: runtimeOutputName })
        }
        error={invalidRuntimeOutputName}
      />
    </Stack>
  );
};

type SchemaSelectProps = {
  outputType: DatasetType;
  schemas: AutomationJsonSchema[];
  value?: AutomationSchemaRef | null;
  error?: string;
  onChange: (schemaRef: AutomationSchemaRef | null) => void;
};

const TransformOutputSchemaSelect = ({
  outputType,
  schemas,
  value,
  error,
  onChange,
}: SchemaSelectProps) => {
  const valueString = schemaRefToValue(value);
  const compatibleSchemas = useMemo(
    () => schemas.filter((schema) => schemaSupportsOutputType(schema, outputType)),
    [outputType, schemas],
  );
  const selectedSchema = value ? schemas.find((schema) => schemaRefMatches(schema, value)) : null;
  const selectedSchemaIsCompatible =
    !value || Boolean(selectedSchema && schemaSupportsOutputType(selectedSchema, outputType));

  const options = useMemo(() => {
    const schemaOptions = compatibleSchemas.map((schema) => ({
      value: schemaRefToValue(schema)!,
      label: makeSchemaLabel(schema),
    }));

    if (value && !selectedSchemaIsCompatible) {
      return [
        ...schemaOptions,
        {
          value: schemaRefToValue(value)!,
          label: selectedSchema
            ? `Incompatible Schema (${makeSchemaLabel(value)})`
            : `Deleted Schema (${makeSchemaLabel(value)})`,
        },
      ];
    }

    return schemaOptions;
  }, [compatibleSchemas, selectedSchema, selectedSchemaIsCompatible, value]);

  if (!outputTypeSupportsSchemaRef(outputType)) {
    return null;
  }

  return (
    <StyledSelect
      label="Output Schema"
      placeholder={compatibleSchemas.length > 0 ? 'Select an output schema' : 'No compatible schemas defined'}
      data={options}
      value={valueString}
      onChange={(nextValue) => onChange(valueToSchemaRef(nextValue))}
      required={false}
      clearable={true}
      allowDeselect={true}
      disabled={compatibleSchemas.length === 0 && !value}
      error={
        error ||
        (selectedSchemaIsCompatible
          ? undefined
          : selectedSchema
            ? 'File outputs require a schema with root type `string`'
            : 'Selected schema is no longer defined')
      }
    />
  );
};

export default TransformDataOutputFields;
